import { FuturesAsSpotToggle } from "features/bots/BotForm/Advanced/FollowFuturesAsSpot";
import { FollowFuturesAsSpotData } from "@shared/features/bots/BotForm/common/hooks/useFollowFuturesAsSpot";
import React, { useContext } from "react";
import CustomInputNumber from "common/Inputs/CustomInputNumber.tsx";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid.tsx";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles.ts";
import { HelperTooltip } from "common/Tooltips/HelperTooltip.tsx";
import useMeasure from "utils/hooks/useMeasure.ts";
import useAdvanced from "@shared/features/bots/BotForm/useAdvanced.tsx";
import {
  OverrideType,
  Steps,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types.ts";
import SwitchSelect from "features/bots/BotForm/common/SwitchSelect.tsx";
import Text from "common/Typography/Text.tsx";
import styled, { useTheme } from "styled-components";
import {
  CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE,
  OPERATION_HOURS_TITLE,
} from "@shared/features/bots/BotForm/common/constants.ts";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle.tsx";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { BotType, LanguageNameSpaces } from "@shared/types";
import RestoreChangesButton from "features/bots/BotForm/common/RestoreChangesButton.tsx";
import { SectionTitle } from "features/bots/BotForm/common/styles.ts";
import IntercomLink from "common/Text/IntercomLink.tsx";
import { openMessage } from "utils/misc.tsx";
import Select from "common/Select/Select.tsx";
import AccountConfigurationPreview from "features/bots/BotForm/Advanced/AccountConfigurationPreview.tsx";
import AlternativeUsdPairs from "features/bots/BotForm/General/AlternativeUsdPairs.tsx";
import { IdeaIcon } from "common/Icons/Icons.tsx";
import ExportOutlined from "@ant-design/icons/ExportOutlined";
import OperationHoursDrawer from "features/bots/BotForm/OperationHours/OperationHoursDrawer.tsx";
import { StylableButton } from "common/Buttons/Button.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import AutoCancelTradeTimeout from "features/bots/BotForm/Advanced/AutoCancelTradeTimeout.tsx";
import KeywordToSymbol from "features/bots/BotForm/Advanced/KeywordToSymbol.tsx";

const Advanced = ({
  futuresAsSpot,
}: {
  futuresAsSpot?: FollowFuturesAsSpotData;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const { botType, isChannelConfiguration } = useContext(BotFormContext)!;
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const {
    inputsData,
    stopTypeData,
    stopLimitPriceReductionData,
    showAccountConfiguration,
    operationHoursDrawerOpen,
    setOperationHoursDrawerOpen,
    autoCancelTimeoutError,
  } = useAdvanced({ openMessage });
  const isSignalsBot = botType === BotType.SIGNAL;
  // New look: one 16px gap between fields, as in the other steps.
  const fieldGap = theme.isNewLook ? 16 : 20;

  const getStopLimitPriceReduction = () => {
    if (!stopLimitPriceReductionData.shouldDisplay) return;
    return (
      <div>
        <FormFieldTitle>{stopLimitPriceReductionData.title}</FormFieldTitle>
        <CustomInputNumber
          onChange={stopLimitPriceReductionData.onChange}
          value={stopLimitPriceReductionData.value}
          bordered={stopLimitPriceReductionData.bordered}
          placeholder={stopLimitPriceReductionData.placeholder}
          precision={stopLimitPriceReductionData.precision}
          suffix={stopLimitPriceReductionData.suffix}
          stringMode={true}
          useDebounce={true}
        />
        {stopLimitPriceReductionData.error && (
          <ErrorText>{stopLimitPriceReductionData.error}</ErrorText>
        )}
        {stopLimitPriceReductionData.warning && (
          <ErrorText style={{ color: theme.colors.gold }}>
            {stopLimitPriceReductionData.warning}{" "}
            <IntercomLink
              articleId="5814866"
              style={{ fontSize: theme.typography.fontSizes.S }}
            />
          </ErrorText>
        )}
      </div>
    );
  };

  const getStopType = () => {
    if (!stopTypeData.shouldDisplay) return;
    return (
      <div>
        <FormFieldTitle>
          {stopTypeData.title}
          <HelperTooltip {...stopTypeData.helperData} />
        </FormFieldTitle>
        {stopTypeData.isDropdown ? (
          <Select
            style={{ width: "100%" }}
            value={stopTypeData.value}
            onChange={stopTypeData.onChange}
            popupMatchSelectWidth={false}
            optionLabelProp="label"
            placeholder={stopTypeData.placeholder}
            options={stopTypeData.options.map((option) => ({
              value: option,
              label: option,
            }))}
            mobileTitle={stopTypeData.title}
          />
        ) : (
          <SwitchSelect
            options={stopTypeData.options}
            onClick={stopTypeData.onChange}
            selectedOption={stopTypeData.value}
            invalidOptions={stopTypeData.invalidOptions}
          />
        )}
        {stopTypeData.error && <ErrorText>{stopTypeData.error}</ErrorText>}
      </div>
    );
  };

  const inputs = inputsData.map((inputData) => {
    const title = (
      <FormFieldTitle>
        {inputData.title}
        {inputData.helperData && <HelperTooltip {...inputData.helperData} />}
      </FormFieldTitle>
    );
    if (inputData.key === "auto_cancel_timeout_seconds") {
      return (
        <div key={inputData.key}>
          {title}
          <AutoCancelTradeTimeout errorMessage={autoCancelTimeoutError} />
        </div>
      );
    }
    return (
      <div key={inputData.key}>
        {title}
        <CustomInputNumber
          onChange={inputData.onChange}
          value={inputData.value}
          bordered={inputData.bordered}
          placeholder={inputData.placeholder}
          precision={inputData.precision}
          suffix={
            inputData.suffix && <Text type="secondary">{inputData.suffix}</Text>
          }
          stringMode={true}
          useDebounce={true}
        />
        {inputData.error && <ErrorText>{inputData.error}</ErrorText>}
        {inputData.infoMessage && (
          <InfoMessageWrapper>
            <IdeaIcon
              fillColor={theme.colors.primaryBlue}
              style={{
                width: 24,
                height: 24,
              }}
            />
            <InfoText>{inputData.infoMessage}</InfoText>
          </InfoMessageWrapper>
        )}
      </div>
    );
  });

  const getSignalsBotItems = () => {
    if (!isSignalsBot) return;
    return (
      <>
        <TitleWithToggle
          title={
            <FormFieldTitle style={{ margin: 0 }}>
              {t(CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE)}
            </FormFieldTitle>
          }
          fieldType={ToggleField.CLOSE_ON_TP_BEFORE_ENTRY}
          style={{ marginTop: isChannelConfiguration ? 0 : fieldGap }}
        />
        {isChannelConfiguration && (
          <Text $size="S" type="secondary" style={{ marginTop: 8 }}>
            {t("messages.info.shared_parameter_spot_margin")}
          </Text>
        )}
        <AlternativeUsdPairs style={{ marginTop: 16 }} />
        {futuresAsSpot && (
          <FuturesAsSpotToggle data={futuresAsSpot} style={{ marginTop: 16 }} />
        )}
        <KeywordToSymbol style={{ marginTop: 16 }} />
      </>
    );
  };

  const operationHours = (
    <TitleWithToggle
      title={
        <FormFieldTitle style={{ margin: 0 }}>
          {t(OPERATION_HOURS_TITLE)}
        </FormFieldTitle>
      }
      fieldType={ToggleField.OPERATION_HOURS}
      style={{
        display: "flex",
        borderTop: theme.borders.light,
        paddingTop: 20,
        marginTop: 20,
        lineHeight: "32px",
      }}
      titleStyle={{ flex: 1 }}
      contentStyle={{ margin: 0, width: "fit-content" }}
      onOverrideChange={(type?: OverrideType) =>
        setOperationHoursDrawerOpen(type === OverrideType.PERSONAL)
      }
      content={
        <LinkButton onClick={() => setOperationHoursDrawerOpen(true)}>
          {width < 450
            ? t("operation_hours.edit")
            : t("operation_hours.edit_schedule")}
          <ExportIcon />
        </LinkButton>
      }
    />
  );

  return (
    <div style={{ position: "relative" }}>
      <RestoreChangesButton
        tab={Steps.ADVANCED}
        title={<SectionTitle>Advanced</SectionTitle>}
      />
      <EqualColumnsGrid
        numColumns={width < 510 ? 1 : 2}
        rowGap={fieldGap}
        ref={measureRef}
      >
        {inputs}
      </EqualColumnsGrid>
      {getSignalsBotItems()}
      {stopTypeData.shouldDisplay && (
        <EqualColumnsGrid
          numColumns={2}
          rowGap={fieldGap}
          ref={measureRef}
          style={{ marginTop: botType === BotType.GRID ? 0 : fieldGap }}
        >
          {getStopType()}
          {getStopLimitPriceReduction()}
        </EqualColumnsGrid>
      )}
      {isSignalsBot && !isChannelConfiguration && operationHours}
      {showAccountConfiguration && <AccountConfigurationPreview />}
      <OperationHoursDrawer
        open={operationHoursDrawerOpen}
        closeDrawer={() => setOperationHoursDrawerOpen(false)}
      />
    </div>
  );
};

export default React.memo(Advanced);

const InfoMessageWrapper = styled.div`
  background-color: ${(p) =>
    p.theme.isDarkMode
      ? p.theme.colors.mediumBgColor
      : p.theme.colors.statisticsBgColor};
  border-radius: 8px;
  padding: 4px 8px;
  margin-top: 8px;
  display: flex;
  align-items: center;
`;
const InfoText = styled(Text).attrs({ $size: "S" })`
  margin-left: 8px;
  flex: 1;
  color: ${(p) =>
    p.theme.isDarkMode
      ? p.theme.colors.mediumText
      : p.theme.colors.primaryText};
`;
const LinkButton = styled(StylableButton).attrs({ type: "link" })`
  text-align: right;
  flex: 1;
  margin-top: 0;
`;
const ExportIcon = styled(ExportOutlined)`
  margin-left: 6px;
`;
